import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  BookOpen, Search, Plus, Edit, Trash2, ChevronRight, 
  Loader2, FileText, HelpCircle, Rocket, Target, Image,
  BarChart3, Receipt, AlertTriangle, X, Save
} from "lucide-react";

const categories = [
  { value: "getting_started", label: "Getting Started", icon: Rocket },
  { value: "campaigns", label: "Campaigns", icon: Target },
  { value: "creatives", label: "Creatives", icon: Image },
  { value: "targeting", label: "Targeting", icon: Target },
  { value: "billing", label: "Billing & Payments", icon: Receipt },
  { value: "analytics", label: "Analytics & Reporting", icon: BarChart3 },
  { value: "faq", label: "FAQs", icon: HelpCircle },
];

const visibilityOptions = [
  { value: "ALL", label: "Everyone" },
  { value: "ADVERTISERS", label: "Advertisers Only" },
  { value: "ADMINS", label: "Admins Only" },
];

// Simple markdown renderer
const renderMarkdown = (content) => {
  if (!content) return null;
  
  // Convert markdown to HTML (basic support)
  let html = content
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-6 mb-3">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-3 rounded-lg my-2 overflow-x-auto text-sm"><code>$1</code></pre>')
    .replace(/`(.*?)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm">$1</code>')
    // Lists
    .replace(/^\- (.*$)/gim, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline hover:no-underline" target="_blank">$1</a>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-3">')
    .replace(/\n/g, '<br/>');
  
  return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: `<p class="mb-3">${html}</p>` }} />;
};

export default function Resources() {
  const { token, user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedResource, setSelectedResource] = useState(null);
  
  // Admin editing state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "getting_started",
    tags: "",
    visibility: "ALL",
    order: 0
  });

  const isAdmin = user?.role === "MASTER_ADMIN" || user?.role === "BOOMA_ADMIN";

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
    } catch (error) {
      toast.error("Failed to load resources");
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = useMemo(() => {
    return resources.filter(resource => {
      const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
      const matchesSearch = !searchQuery || 
        resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [resources, selectedCategory, searchQuery]);

  const groupedResources = useMemo(() => {
    const groups = {};
    filteredResources.forEach(resource => {
      if (!groups[resource.category]) {
        groups[resource.category] = [];
      }
      groups[resource.category].push(resource);
    });
    return groups;
  }, [filteredResources]);

  const openCreateModal = () => {
    setEditingResource(null);
    setFormData({
      title: "",
      content: "",
      category: "getting_started",
      tags: "",
      visibility: "ALL",
      order: 0
    });
    setEditModalOpen(true);
  };

  const openEditModal = (resource) => {
    setEditingResource(resource);
    setFormData({
      title: resource.title,
      content: resource.content,
      category: resource.category,
      tags: resource.tags?.join(", ") || "",
      visibility: resource.visibility,
      order: resource.order || 0
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (resource) => {
    setEditingResource(resource);
    setDeleteModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
      };

      if (editingResource) {
        await axios.put(`${API}/resources/${editingResource.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Resource updated");
      } else {
        await axios.post(`${API}/resources`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Resource created");
      }
      
      setEditModalOpen(false);
      fetchResources();
    } catch (error) {
      toast.error("Failed to save resource");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingResource) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/resources/${editingResource.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Resource deleted");
      setDeleteModalOpen(false);
      setEditingResource(null);
      if (selectedResource?.id === editingResource.id) {
        setSelectedResource(null);
      }
      fetchResources();
    } catch (error) {
      toast.error("Failed to delete resource");
    } finally {
      setSaving(false);
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || { label: categoryValue, icon: FileText };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading resources...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resources-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-primary/10">
            <BookOpen className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tight">Resources</h1>
            <p className="text-muted-foreground">
              Knowledge base and guides to help you get the most out of Booma Partner Portal
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={openCreateModal} data-testid="create-resource-btn">
            <Plus className="mr-2 h-4 w-4" />
            New Article
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search articles..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-10"
            data-testid="resources-search"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Articles List */}
        <div className="lg:col-span-1 space-y-4">
          {selectedCategory === "all" ? (
            // Grouped by category
            Object.entries(groupedResources).map(([category, items]) => {
              const catInfo = getCategoryInfo(category);
              const CatIcon = catInfo.icon;
              return (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                    <CatIcon className="h-4 w-4" />
                    {catInfo.label}
                  </h3>
                  <Card className="bg-card border-border">
                    <CardContent className="p-0 divide-y divide-border">
                      {items.map(resource => (
                        <div
                          key={resource.id}
                          className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors flex items-center justify-between ${
                            selectedResource?.id === resource.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                          }`}
                          onClick={() => setSelectedResource(resource)}
                          data-testid={`resource-item-${resource.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{resource.title}</p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              );
            })
          ) : (
            // Single category view
            <Card className="bg-card border-border">
              <CardContent className="p-0 divide-y divide-border">
                {filteredResources.map(resource => (
                  <div
                    key={resource.id}
                    className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors flex items-center justify-between ${
                      selectedResource?.id === resource.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                    }`}
                    onClick={() => setSelectedResource(resource)}
                    data-testid={`resource-item-${resource.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{resource.title}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {filteredResources.length === 0 && (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No articles found</h3>
                <p className="text-muted-foreground text-sm">
                  {searchQuery ? "Try a different search term" : "No resources in this category yet"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Article Content */}
        <div className="lg:col-span-2">
          {selectedResource ? (
            <Card className="bg-card border-border">
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{getCategoryInfo(selectedResource.category).label}</Badge>
                      {selectedResource.visibility !== "ALL" && isAdmin && (
                        <Badge variant="secondary" className="text-xs">
                          {selectedResource.visibility === "ADMINS" ? "Admin Only" : "Advertisers Only"}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-2xl">{selectedResource.title}</CardTitle>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(selectedResource)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => openDeleteModal(selectedResource)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {renderMarkdown(selectedResource.content)}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-card border-border h-full min-h-[400px] flex items-center justify-center">
              <CardContent className="text-center">
                <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="font-semibold text-lg mb-2">Select an article</h3>
                <p className="text-muted-foreground">
                  Choose an article from the list to read its content
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription>
              {editingResource ? "Update the article content below" : "Create a new knowledge base article"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input 
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Article title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="visibility">Visibility</Label>
                <Select value={formData.visibility} onValueChange={(v) => setFormData(prev => ({ ...prev, visibility: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {visibilityOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input 
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input 
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="onboarding, setup, basics"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content * (Markdown supported)</Label>
              <Textarea 
                id="content"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your article content here... Supports **bold**, *italic*, # headers, - lists, and `code`"
                className="min-h-[300px] font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingResource ? "Update Article" : "Create Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Article
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{editingResource?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
